import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Divider, Drawer, Empty, Space, Typography } from 'antd';
import { ReadOutlined } from '@ant-design/icons';
import { getGlossary, getSurfaceDoc } from './contentLoader';
import { closeHelpPanel, openHelpPanel, useHelpPanelOpen } from './helpPanelStore';
import { InlineMd, MdBlocks, type TermRef } from './markdown';
import { surfaceForPath, type SurfaceId } from './surfaceRegistry';
import { Term } from './Term';

const { Title, Text } = Typography;

/** True when the key event originated in a place where the user is typing. */
function isTypingTarget(el: EventTarget | null): boolean {
  if (!(el instanceof HTMLElement)) return false;
  if (el.isContentEditable) return true;
  const tag = el.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT';
}

function SectionHeading({ children }: { children: string }) {
  return (
    <Title level={5} style={{ marginTop: 16, marginBottom: 6 }}>
      {children}
    </Title>
  );
}

/**
 * The body of the help panel for one surface — exported separately so it is testable without the
 * Drawer chrome. A missing doc renders the honest "not written yet" state (lane 1b delivers the
 * content in parallel), never a crash.
 */
export function HelpPanelBody({ surfaceId }: { surfaceId: SurfaceId }) {
  const doc = getSurfaceDoc(surfaceId);

  if (!doc) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={
          <Space direction="vertical" size={2}>
            <Text>This page&rsquo;s guide hasn&rsquo;t been written yet.</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Expected at <code className="ol-mono">help/content/{surfaceId}.md</code>.
            </Text>
          </Space>
        }
      />
    );
  }

  // Glossary displays for the terms THIS doc declares — used to make them hoverable inline.
  const glossary = getGlossary();
  const terms: TermRef[] = doc.termIds
    .map((id) => {
      const entry = glossary?.byId.get(id);
      return entry ? { id, display: entry.display } : null;
    })
    .filter((t): t is TermRef => t !== null);

  return (
    <div>
      <Title level={4} style={{ marginTop: 0, marginBottom: 4 }}>
        {doc.title || surfaceId}
      </Title>
      {doc.oneLiner && (
        <Text type="secondary" style={{ fontSize: 13 }}>
          <InlineMd text={doc.oneLiner} terms={terms} />
        </Text>
      )}

      <SectionHeading>Purpose</SectionHeading>
      <div style={{ fontSize: 13 }}>
        <MdBlocks markdown={doc.purpose} terms={terms} />
      </div>

      <SectionHeading>What you can do here</SectionHeading>
      {doc.actionsMd.trim() ? (
        <div style={{ fontSize: 13 }}>
          <MdBlocks markdown={doc.actionsMd} terms={terms} />
        </div>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Nothing documented yet.
        </Text>
      )}

      <SectionHeading>Inputs &amp; fields</SectionHeading>
      {doc.fieldsMd.trim() ? (
        <div style={{ fontSize: 13 }}>
          <MdBlocks markdown={doc.fieldsMd} terms={terms} />
        </div>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          This page has no documented input fields.
        </Text>
      )}

      {doc.extraSections.map((s) => (
        <div key={s.heading}>
          <SectionHeading>{s.heading}</SectionHeading>
          <div style={{ fontSize: 13 }}>
            <MdBlocks markdown={s.content} terms={terms} />
          </div>
        </div>
      ))}

      {doc.termIds.length > 0 && (
        <>
          <Divider style={{ margin: '14px 0 10px' }} />
          <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 6 }}>
            Terms on this page (hover for definitions)
          </Text>
          <Space size={[6, 6]} wrap>
            {doc.termIds.map((id) => (
              <Term key={id} id={id} />
            ))}
          </Space>
        </>
      )}
    </div>
  );
}

/**
 * Global HelpPanel host (OPSFL-HELP-1): mounted ONCE in the app shell, keyed off the route —
 * zero per-page edits. Opens from the `?` keyboard shortcut (suppressed while typing) and from
 * HelpMenu's "About this page".
 */
export function HelpPanelHost() {
  const location = useLocation();
  const open = useHelpPanelOpen();
  const surfaceId = useMemo(() => surfaceForPath(location.pathname), [location.pathname]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== '?' || e.ctrlKey || e.metaKey || e.altKey || e.repeat) return;
      if (isTypingTarget(e.target)) return;
      e.preventDefault();
      openHelpPanel();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <Drawer
      title={
        <Space>
          <ReadOutlined />
          <span>About this page</span>
        </Space>
      }
      placement="right"
      width={420}
      open={open}
      onClose={closeHelpPanel}
    >
      {/* Keyed off the surface so navigating while open swaps the doc cleanly. */}
      <HelpPanelBody key={surfaceId} surfaceId={surfaceId} />
    </Drawer>
  );
}
